import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH_ROOT } from '@/routes/paths';
import { PersonAdd as InviteIcon } from '@mui/icons-material';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { InviteMembersModal } from '@/components/organization';
import { OrganizationOverviewCard } from '@/components/organization/OrganizationOverviewCard';
import { InvitationCard } from '@/components/organization/InvitationCard';
import { useDemoOrgData } from '@/hooks/organization/useDemoOrgData';
import { DEF_JAM_ORG_ID } from '@/mockData/common/demoFilters';
import { notifyDemoAction } from '@/utils/demoNotifications';
import { useOrgInvites, useOrgRoles } from 'src/hooks/organization';
import { useOrg } from 'src/contexts/OrgContext';
import { useDemoOrg } from 'src/contexts/DemoOrgContext';
import { useProfile } from 'src/hooks/profile/useProfile';

interface OrganizationInvitationsProps {
  isDemo?: boolean;
}

const OrganizationInvitations: React.FC<OrganizationInvitationsProps> = ({ isDemo = false }) => {
  const navigate = useNavigate();
  const { selectedOrgId } = useOrg();
  const demoOrgContext = useDemoOrg();
  const { profile } = useProfile();
  const { getOrganizationById, getInvitationsByOrgId } = useDemoOrgData();

  const orgId = isDemo ? demoOrgContext?.selectedOrg?.orgId : selectedOrgId;
  const activeOrgId = isDemo ? (orgId ?? DEF_JAM_ORG_ID) : orgId;
  const organization = getOrganizationById(activeOrgId ?? DEF_JAM_ORG_ID);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const isPersonalOrg = !isDemo && activeOrgId === profile?.userId;

  const {
    invites: apiInvites,
    createInvite,
    cancelInvite,
    resendInvite,
    acceptInvite,
    declineInvite,
  } = useOrgInvites(isDemo ? undefined : activeOrgId);
  const { customRoles } = useOrgRoles(isDemo ? undefined : activeOrgId);

  const demoInvitations = getInvitationsByOrgId(activeOrgId ?? DEF_JAM_ORG_ID);
  const invitations = isDemo ? demoInvitations : apiInvites;
  const filteredInvitations = activeFilter === 'all'
    ? invitations
    : invitations.filter((invite) => invite.status === activeFilter);

  const statusCounts = {
    pending: invitations.filter((invite) => invite.status === 'pending').length,
    accepted: invitations.filter((invite) => invite.status === 'accepted').length,
    declined: invitations.filter((invite) => invite.status === 'declined').length,
    expired: invitations.filter((invite) => invite.status === 'expired').length,
  };

  const handleInviteSubmit = async (
    email: string,
    roleId: string | undefined,
    customRoleId: string | undefined,
  ) => {
    if (isDemo) {
      notifyDemoAction(`Invite ${email}`, isDemo);
      return;
    }

    await createInvite(email, roleId, customRoleId);
    setShowInviteModal(false);
  };

  const callDemoOr = async (
    label: string,
    action: () => Promise<void>,
  ) => {
    if (isDemo) {
      notifyDemoAction(label, isDemo);
      return;
    }

    await action();
  };

  return (
    <div className="max-w-4xl mx-auto px-6 py-8">
      <div className="mb-8">
        <button
          onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationManagement(activeOrgId || '') : PATH_ROOT.organization.management)}
          className="mb-4 flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              {isPersonalOrg ? 'Your Invitations' : 'Organization Invitations'}
            </h1>
            <p className="mt-1 text-muted-foreground">
              {isPersonalOrg
                ? 'View and manage invitations to join organizations'
                : 'Manage pending invitations and view invitation history'}
            </p>
          </div>
          {!isPersonalOrg && (
            <Button
              variant="default"
              size="lg"
              onClick={() => setShowInviteModal(true)}
            >
              <InviteIcon sx={{ fontSize: 16, marginRight: '8px' }} />
              Send Invitations
            </Button>
          )}
        </div>
      </div>

      {organization && <OrganizationOverviewCard organization={organization} />}

      <div className="mb-6 flex gap-3 overflow-x-auto">
        {[
          { key: 'all', label: 'All', count: invitations.length },
          { key: 'pending', label: 'Pending', count: statusCounts.pending },
          { key: 'accepted', label: 'Accepted', count: statusCounts.accepted },
          { key: 'declined', label: 'Declined', count: statusCounts.declined },
          { key: 'expired', label: 'Expired', count: statusCounts.expired },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveFilter(tab.key)}
            className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all duration-200 ${
              activeFilter === tab.key
                ? 'border border-border bg-muted text-foreground'
                : 'border border-border text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {tab.label} {tab.count > 0 && `(${tab.count})`}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {filteredInvitations.map((invitation) => (
          <InvitationCard
            key={invitation.orgInviteId}
            inviteId={invitation.orgInviteId}
            invitedEmail={invitation.invitedEmail}
            role={invitation.role}
            customRoleName={invitation.customRoleName}
            invitedByName={invitation.invitedByProfile?.displayName || 'Unknown'}
            status={invitation.status}
            createdDate={invitation.createdAt}
            expiresDate={invitation.expiresAt}
            isIncoming={isPersonalOrg}
            onResend={!isPersonalOrg ? (inviteId) => callDemoOr('Resend invitation', () => resendInvite(inviteId)) : undefined}
            onCancel={!isPersonalOrg ? (inviteId) => callDemoOr('Cancel invitation', () => cancelInvite(inviteId)) : undefined}
            onAccept={isPersonalOrg ? (inviteId) => callDemoOr('Accept invitation', () => acceptInvite(inviteId)) : undefined}
            onDecline={isPersonalOrg ? (inviteId) => callDemoOr('Decline invitation', () => declineInvite(inviteId)) : undefined}
          />
        ))}
      </div>

      {filteredInvitations.length === 0 && (
        <div className="py-16 text-center text-muted-foreground">
          <InviteIcon className="mx-auto mb-4 h-12 w-12 opacity-50 text-muted-foreground" />
          <h3 className="mb-2 text-lg text-muted-foreground">
            No {activeFilter !== 'all' ? activeFilter : ''} invitations
          </h3>
          <p className="mb-6">
            {isPersonalOrg
              ? (activeFilter === 'all'
                ? 'You have no pending invitations to join organizations'
                : `No ${activeFilter} invitations found`)
              : (activeFilter === 'all'
                ? 'Send your first invitation to start building your team'
                : `No ${activeFilter} invitations found`)}
          </p>
          {!isPersonalOrg && (
            <Button
              onClick={() => setShowInviteModal(true)}
              variant="default"
              size="lg"
            >
              <InviteIcon sx={{ fontSize: 16, marginRight: '8px' }} />
              Send Invitations
            </Button>
          )}
        </div>
      )}

      <div className="mt-8 flex gap-3">
        <Button
          variant="outline"
          size="lg"
          onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationMembers(activeOrgId || '') : PATH_ROOT.organization.members)}
        >
          View Members
        </Button>
        <Button
          variant="ghost"
          size="lg"
          onClick={() => navigate(isDemo ? PATH_ROOT.demo.organizationManagement(activeOrgId || '') : PATH_ROOT.organization.management)}
        >
          Back to Management
        </Button>
      </div>

      {showInviteModal && (
        <InviteMembersModal
          isDemo={isDemo}
          orgId={activeOrgId || ''}
          customRoles={customRoles}
          onClose={() => setShowInviteModal(false)}
          onSubmit={handleInviteSubmit}
        />
      )}
    </div>
  );
};

export default OrganizationInvitations;
